import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { mockData } from '../data/mock';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  CheckCircle2,
  Users,
  BookOpen,
  HelpCircle,
  Building2,
  ArrowRight,
  Shield,
  Heart,
  Activity,
  Stethoscope,
  Siren,
  BadgeCheck,
  ChevronRight,
} from 'lucide-react';

const ContentTabs = () => {
  const { t, language } = useLanguage();
  const [activeTab, setActiveTab] = useState('targetGroup');

  const tabs = [
    { id: 'targetGroup', icon: Users, label: t('tabs.targetGroup') },
    { id: 'content', icon: BookOpen, label: t('tabs.content') },
    { id: 'faq', icon: HelpCircle, label: t('tabs.faq') },
    { id: 'bgCourse', icon: Building2, label: t('tabs.bgCourse') },
  ];

  const contentIcons = [
    Shield,
    Heart,
    Activity,
    Stethoscope,
    Shield,
    Heart,
    Siren,
    Activity,
    Heart,
    Stethoscope,
    Siren,
  ];

  return (
    <section className="section-padding bg-white overflow-hidden">
      <div className="container-custom">
        {/* Mobile: Apple-style pill navigation */}
        <div className="md:hidden mb-8">
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide -mx-4 px-4">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center gap-2 px-5 py-3 rounded-full whitespace-nowrap
                  font-medium text-sm transition-all duration-300 ease-out
                  ${activeTab === tab.id 
                    ? 'bg-red-600 text-white shadow-lg shadow-red-600/30 scale-105' 
                    : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                  }
                `}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Desktop: Enhanced tab navigation */}
        <div className="hidden md:block mb-10">
          <div className="inline-flex bg-neutral-100 p-1.5 rounded-2xl">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center gap-2.5 px-6 py-3.5 rounded-xl
                  font-medium transition-all duration-300 ease-out
                  ${activeTab === tab.id 
                    ? 'bg-white text-red-600 shadow-lg' 
                    : 'text-neutral-600 hover:text-neutral-900'
                  }
                `}
              >
                <tab.icon className={`w-5 h-5 transition-transform duration-300 ${activeTab === tab.id ? 'scale-110' : ''}`} />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content with smooth transitions */}
        <div className="relative">
          {/* Target Group Tab */}
          <div className={`transition-all duration-500 ease-out ${activeTab === 'targetGroup' ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 absolute inset-0 pointer-events-none'}`}>
            {activeTab === 'targetGroup' && (
              <div className="bg-gradient-to-br from-neutral-50 to-red-50/30 rounded-3xl p-6 md:p-10">
                <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 mb-6">
                  {t('targetGroup.title')}
                </h2>
                <div className="space-y-4 text-neutral-600 leading-relaxed">
                  <p>{t('targetGroup.description')}</p>
                  <p>{t('targetGroup.compliance')}</p>
                </div>

                {/* License Classes - Apple style cards */}
                <div className="mt-8">
                  <h3 className="text-sm font-semibold text-neutral-500 uppercase tracking-wider mb-4">
                    {language === 'de' ? 'Führerscheinklassen' : 'License Classes'}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {['A', 'A1', 'A2', 'B', 'BE', 'C', 'C1', 'CE', 'C1E', 'D', 'D1', 'DE', 'D1E', 'L', 'T', 'M'].map(
                      (cls, idx) => (
                        <span
                          key={cls}
                          className="px-4 py-2 bg-white rounded-xl text-sm font-semibold text-neutral-700 shadow-sm border border-neutral-100 hover:shadow-md hover:scale-105 transition-all duration-200"
                          style={{ animationDelay: `${idx * 50}ms` }}
                        >
                          {cls}
                        </span>
                      )
                    )}
                  </div>
                </div>

                {/* Certifications - Modern badges */}
                <div className="mt-8 flex flex-wrap gap-3">
                  {[
                    { icon: BadgeCheck, label: '§19 FeV' },
                    { icon: BadgeCheck, label: 'DGUV 304-001' },
                    { icon: BadgeCheck, label: 'DGUV Vorschrift 1' },
                  ].map((cert, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center gap-2 bg-white px-4 py-2.5 rounded-xl shadow-sm border border-neutral-100"
                    >
                      <cert.icon className="w-5 h-5 text-red-600" />
                      <span className="text-sm font-medium">{cert.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Content Tab */}
          <div className={`transition-all duration-500 ease-out ${activeTab === 'content' ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 absolute inset-0 pointer-events-none'}`}>
            {activeTab === 'content' && (
              <div className="bg-gradient-to-br from-neutral-50 to-red-50/30 rounded-3xl p-6 md:p-10">
                <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 mb-4">
                  {t('courseContent.title')}
                </h2>
                <p className="text-neutral-600 mb-6 md:mb-8">
                  {t('courseContent.intro')}
                </p>
                <h3 className="text-sm font-semibold text-neutral-500 uppercase tracking-wider mb-4">
                  {t('courseContent.subtitle')}
                </h3>
                
                {/* Mobile: Stacked cards */}
                <div className="md:hidden space-y-3">
                  {t('courseContent.items').map((item, index) => {
                    const Icon = contentIcons[index] || CheckCircle2;
                    return (
                      <div
                        key={index}
                        className="flex items-center gap-4 bg-white p-4 rounded-2xl shadow-sm border border-neutral-100 active:scale-98 transition-transform"
                      >
                        <div className="p-2.5 bg-red-50 rounded-xl flex-shrink-0">
                          <Icon className="w-5 h-5 text-red-600" />
                        </div>
                        <span className="text-neutral-700 font-medium text-sm leading-snug">{item}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Desktop: Grid */}
                <div className="hidden md:grid md:grid-cols-2 gap-4">
                  {t('courseContent.items').map((item, index) => {
                    const Icon = contentIcons[index] || CheckCircle2;
                    return (
                      <div
                        key={index}
                        className="flex items-start gap-4 bg-white p-5 rounded-2xl border border-neutral-100 hover:shadow-lg hover:scale-[1.02] transition-all duration-300"
                      >
                        <div className="p-2.5 bg-red-50 rounded-xl">
                          <Icon className="w-5 h-5 text-red-600" />
                        </div>
                        <span className="text-neutral-700 font-medium">{item}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* FAQ Tab */}
          <div className={`transition-all duration-500 ease-out ${activeTab === 'faq' ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 absolute inset-0 pointer-events-none'}`}>
            {activeTab === 'faq' && (
              <div className="bg-gradient-to-br from-neutral-50 to-red-50/30 rounded-3xl p-6 md:p-10">
                <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 mb-6 md:mb-8">
                  {t('faq.title')}
                </h2>
                <Accordion type="single" collapsible className="space-y-3">
                  {t('faq.items').map((item, index) => (
                    <AccordionItem
                      key={index}
                      value={`item-${index}`}
                      className="bg-white rounded-2xl border border-neutral-100 px-5 md:px-6 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                    >
                      <AccordionTrigger className="text-left font-semibold text-neutral-900 hover:no-underline py-5 text-sm md:text-base">
                        {item.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-neutral-600 pb-5 text-sm md:text-base leading-relaxed">
                        {item.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            )}
          </div>

          {/* BG Course Tab */}
          <div className={`transition-all duration-500 ease-out ${activeTab === 'bgCourse' ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 absolute inset-0 pointer-events-none'}`}>
            {activeTab === 'bgCourse' && (
              <div className="bg-gradient-to-br from-neutral-50 to-red-50/30 rounded-3xl p-6 md:p-10">
                <div className="max-w-2xl">
                  <div className="inline-flex items-center gap-2 bg-red-100 text-red-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                    <Building2 className="w-4 h-4" />
                    <span>{language === 'de' ? 'Für Unternehmen' : 'For Companies'}</span>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 mb-4">
                    {t('bgCourses.title')}
                  </h2>
                  <p className="text-neutral-600 mb-8 leading-relaxed">
                    {t('bgCourses.description')}
                  </p>
                  <Link to="/bg-kurse">
                    <Button className="bg-red-600 hover:bg-red-700 text-white font-semibold px-8 py-4 rounded-xl group shadow-lg shadow-red-600/25 hover:shadow-xl hover:shadow-red-600/30 transition-all">
                      <span>{t('bgCourses.cta')}</span>
                      <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContentTabs;
